import type { DragEndEvent } from '@dnd-kit/core';
import type {
  AccessLevelProp,
  GutterContext,
  SizerProp,
  TableHeadCellTooltipProp,
  TableVariantProp,
} from '@meterup/atto';
import type {
  Cell,
  Column,
  ColumnDef,
  ColumnFiltersState,
  FilterFnOption,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  Table as ReactTable,
  TableState as ReactTableState,
  VisibilityState,
} from '@tanstack/react-table';
import type { To } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CELL_SELECTOR_WIDTH,
  CheckboxInput,
  selectors,
  ShadowOnScrollOverflowContents,
  sizer,
  styled,
  Table,
  TableBody,
  TableCell,
  TableCellBuffer,
  TableCellState,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  UtilityControl,
} from '@meterup/atto';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type Dispatch,
  type ElementType,
  type ForwardedRef,
  type JSX,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import type {
  AutoCollectionHandle,
  AutoCollectionStateInterface,
  RowSelectionTracker,
} from './utils';
import { FLEXI_COLUMNS_ANIMATION_MS } from '../FlexiColumns';
import { useFlexiColumnsContext } from '../FlexiColumns/FlexiColumns';
import { EnhancedReactRouterLink } from '../ReactRouterLink';
import {
  createCSVExportHandler,
  createDefaultColumnVisibilityEffect,
  createHeaderSelectionState,
  defaultColumnCanGlobalFilter,
  defaultGlobalFilterFn,
  expandColumnsWithSublineFilters,
  filterFns,
  getRows,
  selectAllToggleHandler,
  sortingFns,
  toggleRowSelection,
  useRowCheckboxHandlers,
  useSelectionTracker,
} from './utils';

export type AutoTableAlignmentProp = 'start' | 'center' | 'end';
export type AutoTableStuckProp = 'top' | 'right' | 'bottom' | 'left';
export type AutoTableOverflowProp = 'auto' | 'hidden' | 'scroll' | 'visible';

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-shadow
  interface ColumnMeta<TData, TValue> {
    isLeading?: boolean;
    alignment?: AutoTableAlignmentProp;
    group?: string | null;
    hasField?: boolean;
    hideLabel?: boolean;
    hideSortIcon?: boolean;
    showOnHover?: boolean;
    minBreakpoint?: 'sm' | 'md' | 'lg' | 'xl';
    tooltip?: TableHeadCellTooltipProp;
    stuck?: AutoTableStuckProp;
    accessLevel?: AccessLevelProp | undefined | null;
    width?: SizerProp;
    maxWidth?: SizerProp;
    minWidth?: SizerProp;
    overflow?: AutoTableOverflowProp;
    hideIfAllNullish?: boolean;
    hideIfAllEqual?: boolean;
    /**
     * Pins column and disables drag reorder.
     * - 'start': at the start, can't be dragged
     * - 'end': at the end, can't be dragged
     * - 'as-in': stays in column order, can't be dragged
     */
    fixedPosition?: 'start' | 'end' | 'as-in' | null;
    alwaysVisible?: boolean;
    /**
     * Function to extract subline content for two-row cells. This function receives the row data
     * and should return the subline content to display below the main cell content.
     * When provided, the cell will display two rows: the main content and the subline content.
     * Both values will be searchable/filterable.
     */
    subline?: (row: TData) => React.ReactNode | null | undefined;
  }
}

const getSizeProps = (_: ReactTable<any>, column: Column<any>) => {
  const meta = column.columnDef.meta ?? {};
  const {
    alignment = 'start',
    group,
    hasField = false,
    hideLabel = false,
    hideSortIcon,
    showOnHover = false,
    width,
    maxWidth,
    minWidth,
    overflow,
  } = meta;

  return {
    alignment,
    group,
    hasField,
    hideLabel,
    hideSortIcon,
    showOnHover,
    style: {
      // eslint-disable-next-line no-nested-ternary
      width: maxWidth ? sizer(maxWidth) : width ? sizer(width) : undefined,
      // eslint-disable-next-line no-nested-ternary
      minWidth: minWidth ? sizer(minWidth) : width ? sizer(width) : undefined,
      overflow,
    },
  };
};

const SELECT_COLUMN_ID = 'select-col';

export const autoTableSelectorWidth = {
  '@notDesktop': '$46',
  '@desktop': '$38',
};

const AutoTableHeadCellSelector = styled(TableHeadCell, {
  '@notDesktop': {
    width: autoTableSelectorWidth['@notDesktop'],
  },

  '@desktop': {
    width: autoTableSelectorWidth['@desktop'],
  },
});
AutoTableHeadCellSelector.displayName = 'AutoTableHeadCellSelector';

const AutoTableCellSelector = styled(TableCell, {
  '@notDesktop': {
    width: autoTableSelectorWidth['@notDesktop'],
  },

  '@desktop': {
    width: autoTableSelectorWidth['@desktop'],
  },
});
AutoTableCellSelector.displayName = 'AutoTableCellSelector';

const AutoTableRowSelectorContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',

  '@notDesktop': {
    width: `$${CELL_SELECTOR_WIDTH - 12 * 2}`,
  },

  '@desktop': {
    width: `$${CELL_SELECTOR_WIDTH - 10 * 2}`,
  },
});
AutoTableRowSelectorContainer.displayName = 'AutoTableRowSelectorContainer';

function AutoTableHeaderSelector({
  headerSelectionState,
  onToggle,
  selectionTracker,
}: {
  headerSelectionState: {
    ariaLabel: string;
    checked: boolean;
    indeterminate: boolean;
  };
  onToggle: () => void;
  selectionTracker?: RowSelectionTracker;
}) {
  return (
    <AutoTableRowSelectorContainer>
      <CheckboxInput
        aria-label={headerSelectionState.ariaLabel}
        checked={headerSelectionState.checked}
        indeterminate={headerSelectionState.indeterminate}
        disabled={false}
        onChange={() => {
          selectionTracker?.setLastSelectedId(undefined);
          onToggle();
        }}
        onClick={(e: MouseEvent<HTMLInputElement>) => {
          e.stopPropagation();
        }}
      />
    </AutoTableRowSelectorContainer>
  );
}

function AutoTableRowSelector<D extends any>({
  row,
  table,
  isRowSelectable = true,
  selectionTracker,
}: {
  row: Row<D>;
  table: ReactTable<D>;
  isRowSelectable?: boolean;
  selectionTracker?: RowSelectionTracker;
}) {
  const { handleChange, handleClick } = useRowCheckboxHandlers(row, table, selectionTracker);

  return (
    <AutoTableRowSelectorContainer>
      <CheckboxInput
        aria-label={row.getIsSelected() ? 'Deselect row' : 'Select row'}
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect() || !row.getCanMultiSelect() || !isRowSelectable}
        onChange={handleChange}
        onClick={handleClick}
      />
    </AutoTableRowSelectorContainer>
  );
}

type GetLinkTo<D extends any> = (row: D) => To | null | undefined;
type IsRowDisabled<D> = (row: D) => boolean;
type IsRowSelected<D> = (row: D) => boolean;
type IsRowCollapsed<D> = (row: D) => boolean;
type IsRowDraggable<D> = (row: D) => boolean;
type OnRowDeselect<D> = (row: D) => void;
type OnRowClick<D> = (row: D) => void;
type OnRowReorder = (fromIndex: number, toIndex: number) => void;

type SubAutoTableProps<D> = {
  data: D;
  isNested: boolean;
};

function AutoTableCellImpl<D extends any, E extends ElementType>({
  as,
  cell,
  table,
  isNavigable,
  isNested,
  to,
  hotkeyTos,
  applyLinkToAllCells,
  hasCollapsableBefore,
  onClick,
  rowHasSubline,
}: {
  as?: E;
  cell: Cell<D, any>;
  table: ReactTable<D>;
  isMultiSelected?: boolean;
  isNavigable?: boolean;
  isNested?: boolean;
  to?: To;
  hotkeyTos?: Record<string, To>;
  applyLinkToAllCells?: boolean;
  hasCollapsableBefore?: boolean;
  onClick?: React.MouseEventHandler<E>;
  rowHasSubline?: boolean;
}) {
  if (cell.column.columnDef.meta?.accessLevel === null) return null;

  const content = flexRender(cell.column.columnDef.cell, cell.getContext());
  const { hideSortIcon, ...sizeProps } = getSizeProps(table, cell.column);

  const meta = cell.column.columnDef.meta ?? {};
  const shouldUseLink = meta.isLeading || applyLinkToAllCells;
  const isNonLeadingWithLink = !meta.isLeading && applyLinkToAllCells;

  // Extract subline content if subline function is provided
  const subline = meta.subline ? meta.subline(cell.row.original) : undefined;

  return (
    <TableCell
      key={cell.id}
      {...sizeProps}
      isLeading={meta.isLeading}
      isNavigable={isNavigable}
      isNested={isNested}
      accessLevel={meta.accessLevel}
      stuck={meta.stuck}
      hasCollapsableBefore={hasCollapsableBefore}
      as={shouldUseLink ? as : undefined}
      to={shouldUseLink ? to : undefined}
      onClick={onClick}
      tabIndex={isNonLeadingWithLink ? -1 : undefined}
      subline={subline}
      rowHasSubline={rowHasSubline}
      // add the prop conditionally like this so react devtools doesn't complain
      {...(shouldUseLink ? { hotkeyTos } : {})}
    >
      {sizeProps.style.overflow === 'auto' || sizeProps.style.overflow === 'scroll' ? (
        <ShadowOnScrollOverflowContents>{content}</ShadowOnScrollOverflowContents>
      ) : (
        content
      )}
    </TableCell>
  );
}

const AutoTableCell = memo(AutoTableCellImpl, (prev, next) => {
  // The `cell` prop returned from TanStack Table's `getVisibleCells()` is always a new
  // reference, so compare the stable values it represents instead of the reference itself
  if (prev.cell.column.id !== next.cell.column.id) return false;
  if (prev.cell.row.original !== next.cell.row.original) return false;

  return (Object.keys(next) as (keyof typeof next)[]).every(
    (key) => key === 'cell' || Object.is(prev[key], next[key]),
  );
}) as typeof AutoTableCellImpl;

interface AutoTableRowProps<D extends any> {
  context?: GutterContext;
  table: ReactTable<D>;
  row: Row<D>;
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  isRowCollapsed?: IsRowCollapsed<Row<D>>;
  onToggleRowCollapse: (row: Row<D>) => void;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  onRowDeselect?: OnRowDeselect<D>;
  selectionTracker?: RowSelectionTracker;
  isMultiSelected?: boolean;
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  applyLinkToAllCells?: boolean;
  'data-index'?: number;
  dragToReorder?: boolean;
  isRowDraggable?: IsRowDraggable<D>;
  onRowReorder?: OnRowReorder;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  isDraggingRow?: boolean;
  isRowHoverable?: boolean;
}

const cellDragWidth = {
  '@notDesktop': '$60',
  '@desktop': '$44',
};

const AutoTableCellDragHandle = styled(UtilityControl, {
  cursor: 'grab',
  opacity: 0.5,

  [selectors.hover]: {
    cursor: 'grabbing',
    opacity: 1,
  },

  [selectors.focus]: {
    cursor: 'grabbing',
    opacity: 1,
  },
});
AutoTableCellDragHandle.displayName = 'AutoTableCellDragHandle';

const AutoTableHeadCellDrag = styled(TableHeadCell, {
  '@notDesktop': {
    widthAll: cellDragWidth['@notDesktop'],
  },

  '@desktop': {
    widthAll: cellDragWidth['@desktop'],
  },
});
AutoTableHeadCellDrag.displayName = 'AutoTableHeadCellDrag';

const AutoTableCellDrag = styled(TableCell, {
  '@notDesktop': {
    widthAll: cellDragWidth['@notDesktop'],
  },

  '@desktop': {
    widthAll: cellDragWidth['@desktop'],
  },
});
AutoTableCellDrag.displayName = 'AutoTableCellDrag';

function AutoTableRowImpl<D extends any>(
  {
    table,
    row,
    getLinkTo,
    getDrawerLinkTo,
    getEditLinkTo,
    isRowDisabled,
    isRowSelected,
    isRowCollapsed,
    onRowClick,
    onRowLeadingClick,
    selectionTracker,
    onRowDeselect,
    onToggleRowCollapse,
    isMultiSelected,
    isNested,
    renderSubTable,
    applyLinkToAllCells,
    'data-index': dataIndex,
    dragToReorder,
    isRowDraggable,
    onRowReorder,
    enableMultiRowSelection,
    isDraggingRow,
    isRowHoverable,
    context = 'application',
    ...restProps
  }: AutoTableRowProps<D>,
  ref?: ForwardedRef<HTMLTableRowElement>,
) {
  const to = getLinkTo?.(row.original);

  const hotkeyTos = useMemo(() => {
    const editTo = getEditLinkTo?.(row.original);
    const drawerTo = getDrawerLinkTo?.(row.original);

    const tos: Record<string, To> = {};

    if (editTo) tos.e = editTo;
    if (drawerTo) tos.d = drawerTo;

    return tos;
  }, [getEditLinkTo, getDrawerLinkTo, row.original]);

  const isDisabled = isRowDisabled?.(row.original) ?? false;
  const isSelected = isRowSelected?.(row.original) ?? false;
  const localCollapsed = isRowCollapsed?.(row) ?? false;
  const isDraggable = isRowDraggable?.(row.original) ?? true;

  // Determine if this specific row should be selectable for multi-row selection
  const isRowSelectable = useMemo(() => {
    if (typeof enableMultiRowSelection === 'function') {
      return enableMultiRowSelection(row);
    }
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection, row]);

  const handleLeadingClick = useCallback(
    () => onRowLeadingClick?.(row.original),
    [onRowLeadingClick, row.original],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLTableRowElement>) => {
      if (!enableMultiRowSelection || !isRowSelectable) {
        onRowClick?.(row.original);
        return;
      }

      if (selectionTracker && (e.shiftKey || e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleRowSelection(row, table, selectionTracker, e.shiftKey);
        return;
      }

      onRowClick?.(row.original);
    },
    [onRowClick, row, table, enableMultiRowSelection, isRowSelectable, selectionTracker],
  );

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !dragToReorder || !isDraggable,
  });

  const onDrag = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleMouseDown = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  }, []);

  // Check if any cell in this row has a subline
  const rowHasSubline = useMemo(
    () =>
      row.getVisibleCells().some((cell) => {
        const meta = cell.column.columnDef.meta ?? {};
        if (meta.subline && typeof meta.subline === 'function') {
          const sublineValue = meta.subline(row.original);
          return sublineValue != null && sublineValue !== '';
        }
        return false;
      }),
    [row],
  );

  const AutoTableRowDragHandle = useCallback(() => {
    if (!dragToReorder) return null;

    return (
      <AutoTableCellDrag rowHasSubline={rowHasSubline}>
        <AutoTableCellDragHandle
          {...attributes}
          {...listeners}
          onMouseDown={handleMouseDown}
          active={isDragging}
          disabled={!isDraggable}
          icon="slide-vertical"
          label="Reorder"
          variant={isMultiSelected ? 'brand' : 'neutral'}
        />
      </AutoTableCellDrag>
    );
  }, [
    dragToReorder,
    attributes,
    listeners,
    isMultiSelected,
    handleMouseDown,
    isDraggable,
    isDragging,
    rowHasSubline,
  ]);

  const subRowData = row.original;
  const subTable = renderSubTable ? renderSubTable({ data: subRowData, isNested: true }) : null;

  const combinedRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (isSelected && node && node.scrollIntoView) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setNodeRef(node);
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      }
    },
    [setNodeRef, ref, isSelected],
  );

  return (
    <>
      <TableRow
        ref={combinedRef}
        key={row.id}
        isDisabled={isDisabled}
        isDragging={isDragging}
        isHoverable={isRowHoverable}
        isSelected={!!(isSelected || isMultiSelected)}
        isNested={isNested}
        onClick={handleClick}
        onMouseDown={(e: MouseEvent<HTMLTableRowElement>) => {
          if (e.shiftKey || e.metaKey || e.ctrlKey) e.preventDefault();
        }}
        data-index={dataIndex}
        data-row-id={row.id}
        style={onDrag}
      >
        <TableCellBuffer context={context} side="leading" />
        <TableCellState side="leading" rowHasSubline={rowHasSubline} />
        {renderSubTable && (
          <TableCell
            collapsable
            onClick={() => onToggleRowCollapse(row)}
            collapsed={localCollapsed}
            stuck="left"
            rowHasSubline={rowHasSubline}
          />
        )}
        {enableMultiRowSelection && (
          <AutoTableCellSelector stuck="left" hasCollapsableBefore={!!renderSubTable}>
            <AutoTableRowSelector
              row={row}
              table={table}
              isRowSelectable={isRowSelectable}
              selectionTracker={selectionTracker}
            />
          </AutoTableCellSelector>
        )}
        {dragToReorder && <AutoTableRowDragHandle />}
        {row.getVisibleCells().map((cell) => (
          <AutoTableCell
            as={to ? EnhancedReactRouterLink : undefined}
            to={to ?? undefined}
            onClick={
              cell.column.columnDef.meta?.isLeading && onRowLeadingClick
                ? handleLeadingClick
                : undefined
            }
            hotkeyTos={to ? hotkeyTos : undefined}
            key={cell.id}
            table={table}
            cell={cell}
            isMultiSelected={isMultiSelected}
            isNested={isNested}
            applyLinkToAllCells={applyLinkToAllCells}
            hasCollapsableBefore={!!renderSubTable}
            rowHasSubline={rowHasSubline}
          />
        ))}
        <TableCellState side="trailing" rowHasSubline={rowHasSubline} />
        <TableCellBuffer context={context} side="trailing" />
      </TableRow>
      {renderSubTable && !localCollapsed && (
        <TableRow {...restProps}>
          <TableCell hasNested colSpan={1000}>
            {subTable}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export const AutoTableRow = forwardRef(AutoTableRowImpl) as <D extends any>(
  props: AutoTableRowProps<D> & { ref?: ForwardedRef<HTMLTableRowElement> },
) => ReturnType<typeof AutoTableRowImpl>;

interface AutoTableBodyProps<D extends any> {
  table: ReactTable<D>;
  rows: Row<D>[];
  multiSelectedRows?: RowSelectionState;
  actions?: ReactNode;
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  isRowCollapsed?: IsRowCollapsed<Row<D>>;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  selectionTracker?: RowSelectionTracker;
  onRowDeselect?: OnRowDeselect<D>;
  onToggleRowCollapse: (row: Row<D>) => void;
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  applyLinkToAllCells?: boolean;
  dragToReorder?: boolean;
  isRowDraggable?: IsRowDraggable<D>;
  onRowReorder?: OnRowReorder;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  isRowHoverable?: boolean;
  context?: GutterContext;
}

type VirtualAutoTableBodyProps<D extends any> = {
  tableContainerRef: RefObject<HTMLDivElement | null>;
} & Omit<AutoTableBodyProps<D>, 'columnDragState'>;

function VirtualAutoTableBody<D extends any>({
  table,
  isRowDisabled,
  isRowSelected,
  isRowCollapsed,
  onToggleRowCollapse,
  isNested,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  onRowClick,
  onRowLeadingClick,
  onRowDeselect,
  selectionTracker,
  multiSelectedRows,
  renderSubTable,
  rows,
  actions,
  tableContainerRef,
  applyLinkToAllCells,
  dragToReorder,
  isRowDraggable,
  onRowReorder,
  enableMultiRowSelection,
  isRowHoverable,
  context = 'application',
}: VirtualAutoTableBodyProps<D>) {
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 36, // based on a precalculated size
    getScrollElement: () => {
      if (typeof tableContainerRef === 'object') {
        return tableContainerRef?.current ?? null;
      }
      return null;
    },
    overscan: 10,
    measureElement: (element) => element?.getBoundingClientRect().height ?? 36,
  });

  // This is a hack which solves the problem of not having the tableContainerRef.current defined.
  // This tends to happen on first and only render as a consequence of our Suspense setup
  // and will subsequently never be be defined unless there is a state change.
  // Results in an AutoTable with no rows when AutoTable is first rendered.
  // This should only be called exactly once.
  useLayoutEffect(() => {
    rowVirtualizer?.measure?.();
  }, [rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  const tableBodyContent = (
    <>
      {paddingTop > 0 && (
        <tr aria-hidden>
          <td style={{ height: `${paddingTop}px` }} />
        </tr>
      )}
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <AutoTableRow
            key={row.id}
            data-index={virtualRow.index}
            ref={(node) => rowVirtualizer.measureElement(node)}
            row={row}
            table={table}
            isRowDisabled={isRowDisabled}
            isRowSelected={isRowSelected}
            isRowCollapsed={isRowCollapsed}
            onToggleRowCollapse={onToggleRowCollapse}
            isNested={isNested}
            getLinkTo={getLinkTo}
            getDrawerLinkTo={getDrawerLinkTo}
            getEditLinkTo={getEditLinkTo}
            onRowClick={onRowClick}
            onRowLeadingClick={onRowLeadingClick}
            onRowDeselect={onRowDeselect}
            selectionTracker={selectionTracker}
            isMultiSelected={multiSelectedRows?.[row.id]}
            renderSubTable={renderSubTable}
            applyLinkToAllCells={applyLinkToAllCells}
            dragToReorder={dragToReorder}
            isRowDraggable={isRowDraggable}
            onRowReorder={onRowReorder}
            enableMultiRowSelection={enableMultiRowSelection}
            isRowHoverable={isRowHoverable}
            context={context}
          />
        );
      })}
      {actions && (
        <TableRow>
          <TableCellBuffer context={context} side="leading" />
          <TableCellState side="leading" />
          <TableCell colSpan={1000}>{actions}</TableCell>
          <TableCellState side="trailing" />
          <TableCellBuffer context={context} side="trailing" />
        </TableRow>
      )}
      {paddingBottom > 0 && (
        <tr aria-hidden>
          <td style={{ height: `${paddingBottom}px` }} />
        </tr>
      )}
    </>
  );

  return <TableBody>{tableBodyContent}</TableBody>;
}

function AutoTableBody<D extends any>({
  table,
  isRowDisabled,
  isRowSelected,
  isRowCollapsed,
  isNested,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  onRowClick,
  onRowLeadingClick,
  selectionTracker,
  onRowDeselect,
  onToggleRowCollapse,
  multiSelectedRows,
  renderSubTable,
  rows,
  actions,
  applyLinkToAllCells,
  dragToReorder,
  isRowDraggable,
  onRowReorder,
  enableMultiRowSelection,
  isRowHoverable,
  context = 'application',
}: AutoTableBodyProps<D>) {
  const tableBodyContent = (
    <>
      {rows.map((row) => (
        <AutoTableRow
          key={row.id}
          row={row}
          table={table}
          isRowDisabled={isRowDisabled}
          isRowSelected={isRowSelected}
          isRowCollapsed={isRowCollapsed}
          onToggleRowCollapse={onToggleRowCollapse}
          isNested={isNested}
          getLinkTo={getLinkTo}
          getDrawerLinkTo={getDrawerLinkTo}
          getEditLinkTo={getEditLinkTo}
          onRowClick={onRowClick}
          onRowLeadingClick={onRowLeadingClick}
          selectionTracker={selectionTracker}
          onRowDeselect={onRowDeselect}
          isMultiSelected={multiSelectedRows?.[row.id]}
          renderSubTable={renderSubTable}
          applyLinkToAllCells={applyLinkToAllCells}
          dragToReorder={dragToReorder}
          isRowDraggable={isRowDraggable}
          onRowReorder={onRowReorder}
          enableMultiRowSelection={enableMultiRowSelection}
          isRowHoverable={isRowHoverable}
          context={context}
        />
      ))}
      {actions && (
        <TableRow>
          <TableCellBuffer context={context} side="leading" />
          <TableCellState side="leading" />
          <TableCell colSpan={1000}>{actions}</TableCell>
          <TableCellState side="trailing" />
          <TableCellBuffer context={context} side="trailing" />
        </TableRow>
      )}
    </>
  );

  return <TableBody>{tableBodyContent}</TableBody>;
}

type CommonAutoTableProps<D> = {
  context?: GutterContext;
  actions?: ReactNode;
  globalFilter?: string;
  globalFilterFn?: FilterFnOption<D>;
  sortingState?: SortingState;
  onChangeSortingState?: Dispatch<SetStateAction<SortingState>>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  data: D[];
  columns: ColumnDef<D, any>[];
  columnVisibility?: VisibilityState;
  columnOrder?: string[];
  onColumnOrderChange?: OnChangeFn<string[]>;
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  onRowDeselect?: OnRowDeselect<D>;
  enableSorting?: boolean;
  enableRowSelection?: boolean | ((row: Row<D>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  enableMultiRowExpansion?: boolean;
  applyLinkToAllCells?: boolean;
  /**
   * Does not need to end with `.csv`, it will be added automatically.
   */
  exportToCSVFilename?: string;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  multiSelectedRows?: RowSelectionState;
  onRowMultiSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (originalRow: D, index: number, parent?: Row<D>) => string;
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  subTableExpandedByDefault?: boolean;
  onRowsChange?: (rows: Row<D>[]) => void;
  emptyState?: ReactNode;
  variant?: TableVariantProp;
  dragToReorder?: boolean;
  /**
   * Function to determine if a specific row should be draggable.
   * Returns true by default (row is draggable).
   * Only applies when dragToReorder is true.
   */
  isRowDraggable?: IsRowDraggable<D>;
  onRowReorder?: OnRowReorder;
  isRowHoverable?: boolean;
};

type AutoTableProps<D> = CommonAutoTableProps<D> & {
  ref?: ForwardedRef<AutoCollectionHandle<D>>;
} & (
    | { isVirtual?: false; tableContainerRef?: false }
    | {
        isVirtual: true;
        tableContainerRef: RefObject<HTMLDivElement | null>;
        renderSubTable?: false;
      }
  ) &
  (
    | {
        rowsOffset: number;
        rowsLimit: number;
      }
    | {
        rowsOffset?: never;
        rowsLimit?: never;
      }
  );

export function AutoTable<D extends any>({
  actions,
  sortingState,
  globalFilter,
  globalFilterFn = defaultGlobalFilterFn,
  columnFilters,
  onColumnFiltersChange,
  columns,
  columnVisibility,
  columnOrder,
  onColumnOrderChange,
  data,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  isRowDisabled,
  isRowSelected,
  onRowDeselect,
  onChangeSortingState,
  onRowClick,
  onRowLeadingClick,
  enableSorting = true,
  enableMultiRowSelection = false,
  exportToCSVFilename,
  enableMultiRowExpansion = true,
  onRowMultiSelectionChange,
  multiSelectedRows,
  getRowId,
  isNested,
  renderSubTable,
  subTableExpandedByDefault = false,
  isVirtual = undefined,
  tableContainerRef = undefined,
  onRowsChange,
  rowsOffset,
  rowsLimit,
  ref,
  emptyState,
  variant,
  applyLinkToAllCells,
  dragToReorder = false,
  isRowDraggable,
  onRowReorder,
  isRowHoverable,
  context = 'application',
}: AutoTableProps<D>) {
  // Memoize state object to prevent unnecessary re-renders
  const state = useMemo(() => {
    const s: Partial<ReactTableState> = {};

    if (globalFilter) {
      s.globalFilter = globalFilter;
    }

    if (multiSelectedRows) {
      s.rowSelection = multiSelectedRows;
    }

    if (sortingState) {
      s.sorting = sortingState;
    }

    if (columnVisibility) {
      s.columnVisibility = columnVisibility;
    }

    if (columnFilters) {
      s.columnFilters = columnFilters;
    }

    if (columnOrder) {
      s.columnOrder = columnOrder;
    }

    return s;
  }, [globalFilter, sortingState, multiSelectedRows, columnVisibility, columnFilters, columnOrder]);

  const isMultiRowSelectionEnabled = useMemo(() => {
    if (typeof enableMultiRowSelection === 'boolean') {
      return enableMultiRowSelection;
    }
    if (typeof enableMultiRowSelection === 'function') {
      return true; // Function-based selection is enabled
    }
    // For AccessLevelProp, we'll need to check permissions
    // For now, we'll assume it's enabled if it's an access level and not null
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection]);

  // Expand columns to include virtual subline filter columns
  const expandedColumns = useMemo(() => expandColumnsWithSublineFilters(columns), [columns]);

  // Memoize table configuration to prevent unnecessary table re-initialization
  const tableConfig = useMemo(
    () => ({
      data,
      columns: expandedColumns,
      filterFns,
      sortingFns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
      enableGlobalFilter: true,
      getColumnCanGlobalFilter: defaultColumnCanGlobalFilter,
      globalFilterFn,
      onSortingChange: onChangeSortingState,
      enableMultiRowSelection: isMultiRowSelectionEnabled,
      onRowSelectionChange: onRowMultiSelectionChange,
      getRowId,
      state,
      onColumnFiltersChange,
      onColumnOrderChange,
    }),
    [
      data,
      expandedColumns,
      enableSorting,
      globalFilterFn,
      onChangeSortingState,
      isMultiRowSelectionEnabled,
      onRowMultiSelectionChange,
      getRowId,
      state,
      onColumnFiltersChange,
      onColumnOrderChange,
    ],
  );

  const table = useReactTable(tableConfig);

  const filteredRows = getRows(table);

  // Computed selection state for the header checkbox
  const headerSelectionState = useMemo(
    () =>
      createHeaderSelectionState(table, filteredRows, enableMultiRowSelection, multiSelectedRows),
    [table, filteredRows, enableMultiRowSelection, multiSelectedRows],
  );

  const handleSelectAllToggle = useCallback(
    () => selectAllToggleHandler(table, enableMultiRowSelection, multiSelectedRows),
    [table, enableMultiRowSelection, multiSelectedRows],
  );

  // Use shared CSV export handler
  const exportToCSV = useMemo(
    () => createCSVExportHandler(table, exportToCSVFilename),
    [table, exportToCSVFilename],
  );

  const { setColumnVisibility } = table;

  // Use shared column visibility effect
  const columnVisibilityEffect = useCallback(
    () =>
      createDefaultColumnVisibilityEffect(
        expandedColumns,
        filteredRows,
        setColumnVisibility,
        columnVisibility,
      ),
    [expandedColumns, filteredRows, setColumnVisibility, columnVisibility],
  );

  useEffect(() => {
    columnVisibilityEffect();
  }, [columnVisibilityEffect]);

  useImperativeHandle(
    ref,
    () => ({
      exportToCSV,
      filteredRows,
      data,
    }),
    [exportToCSV, filteredRows, data],
  );

  useEffect(() => {
    onRowsChange?.(filteredRows);
  }, [filteredRows, onRowsChange]);

  // Memoize rows to prevent unnecessary re-slicing
  const rows = useMemo(
    () =>
      rowsOffset != null && rowsLimit != null
        ? filteredRows.slice(rowsOffset, rowsLimit ? (rowsOffset ?? 0) + rowsLimit : undefined)
        : filteredRows,
    [filteredRows, rowsOffset, rowsLimit],
  );

  const hasSubTable = renderSubTable;
  const [expandedRowsSet, setExpandedRowsSet] = useState(() =>
    subTableExpandedByDefault ? new Set(rows.map((row) => row.id)) : new Set(),
  );

  // Header expand/collapse all: show collapsed (chevron-right) when all rows are collapsed
  const allRowsCollapsed = expandedRowsSet.size === 0;

  useEffect(() => {
    if (subTableExpandedByDefault) {
      setExpandedRowsSet(() => new Set(rows.map((row) => row.id)));
    }
  }, [subTableExpandedByDefault, rows]);

  const onToggleRowCollapse = useCallback(
    (row: Row<D>) => {
      const newExpandedSet = !enableMultiRowExpansion ? new Set() : new Set([...expandedRowsSet]);
      if (expandedRowsSet.has(row.id)) {
        newExpandedSet.delete(row.id);
      } else {
        newExpandedSet.add(row.id);
      }
      setExpandedRowsSet(newExpandedSet);
    },
    [enableMultiRowExpansion, expandedRowsSet],
  );

  const isRowCollapsed = useCallback(
    (row: Row<D>) => !expandedRowsSet.has(row.id),
    [expandedRowsSet],
  );

  // DnD sensors - hooks must be called directly, not in useMemo
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const rowIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const columnIds = useMemo(
    () => table.getVisibleLeafColumns().map((column) => column.id),
    [table],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!onRowReorder || !over || active.id === over.id) {
        return;
      }

      const activeIndex = rows.findIndex((row) => row.id === active.id);
      const overIndex = rows.findIndex((row) => row.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        onRowReorder(activeIndex, overIndex);
      }
    },
    [onRowReorder, rows],
  );

  const [isDraggingRow, setIsDraggingRow] = useState(false);

  // Add hotkey handler for select all when multi-row selection is enabled
  useHotkeys(
    'alt+a',
    (e) => {
      if (!isMultiRowSelectionEnabled) return;
      e.preventDefault();

      // Trigger select all functionality
      handleSelectAllToggle();
    },
    {
      enableOnFormTags: true,
    },
    [isMultiRowSelectionEnabled, handleSelectAllToggle],
  );
  const selectionTracker = useSelectionTracker();

  // Memoize table content to prevent unnecessary re-renders
  const tableContent = useMemo(
    () => (
      <Table isNested={isNested} multiRowSelection={!!isMultiRowSelectionEnabled} variant={variant}>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeadRow key={headerGroup.id}>
              <TableCellBuffer context={context} side="leading" head />
              <TableCellState side="leading" head />
              {hasSubTable && (
                <TableHeadCell
                  collapsable
                  onClick={() => {
                    if (allRowsCollapsed) {
                      setExpandedRowsSet(new Set([...rows.map((row) => row.id)]));
                    } else {
                      setExpandedRowsSet(new Set());
                    }
                  }}
                  collapsed={allRowsCollapsed}
                  stuck="left"
                />
              )}
              {isMultiRowSelectionEnabled && (
                <AutoTableHeadCellSelector
                  key={SELECT_COLUMN_ID}
                  hideSortIcon
                  stuck="left"
                  hasCollapsableBefore={!!hasSubTable}
                >
                  <AutoTableHeaderSelector
                    headerSelectionState={headerSelectionState}
                    onToggle={handleSelectAllToggle}
                    selectionTracker={selectionTracker}
                  />
                </AutoTableHeadCellSelector>
              )}
              {dragToReorder && <AutoTableHeadCellDrag />}
              {headerGroup.headers.map((header) => {
                // Skip the multi-select cell since we render it separately
                if (header.column.id === SELECT_COLUMN_ID) {
                  return null;
                }
                // Skip header for columns hidden in body due to accessLevel (keeps column count in sync)
                if (header.column.columnDef.meta?.accessLevel === null) {
                  return null;
                }
                return (
                  <TableHeadCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    {...getSizeProps(table, header.column)}
                    sortDirection={enableSorting ? header.column.getIsSorted() : undefined}
                    hideSortIcon={!enableSorting}
                    accessLevel={header.column.columnDef.meta?.accessLevel}
                    isLeading={header.column.columnDef.meta?.isLeading}
                    stuck={header.column.columnDef.meta?.stuck}
                    tooltip={header.column.columnDef.meta?.tooltip}
                    hasCollapsableBefore={!!hasSubTable}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHeadCell>
                );
              })}
              <TableCellState side="trailing" head />
              <TableCellBuffer context={context} side="trailing" head />
            </TableHeadRow>
          ))}
        </TableHead>
        {isVirtual && tableContainerRef ? (
          <VirtualAutoTableBody
            table={table}
            rows={rows}
            tableContainerRef={tableContainerRef}
            isRowDisabled={isRowDisabled}
            isRowSelected={isRowSelected}
            isRowCollapsed={isRowCollapsed}
            onToggleRowCollapse={onToggleRowCollapse}
            isNested={isNested}
            getLinkTo={getLinkTo}
            getDrawerLinkTo={getDrawerLinkTo}
            getEditLinkTo={getEditLinkTo}
            onRowClick={onRowClick}
            onRowLeadingClick={onRowLeadingClick}
            selectionTracker={selectionTracker}
            onRowDeselect={onRowDeselect}
            multiSelectedRows={multiSelectedRows}
            renderSubTable={renderSubTable}
            actions={actions}
            applyLinkToAllCells={applyLinkToAllCells}
            dragToReorder={dragToReorder}
            isRowDraggable={isRowDraggable}
            onRowReorder={onRowReorder}
            enableMultiRowSelection={enableMultiRowSelection}
            isRowHoverable={isRowHoverable}
            context={context}
          />
        ) : (
          <AutoTableBody
            table={table}
            rows={rows}
            isRowDisabled={isRowDisabled}
            isRowSelected={isRowSelected}
            isRowCollapsed={isRowCollapsed}
            onToggleRowCollapse={onToggleRowCollapse}
            isNested={isNested}
            getLinkTo={getLinkTo}
            getDrawerLinkTo={getDrawerLinkTo}
            getEditLinkTo={getEditLinkTo}
            onRowClick={onRowClick}
            onRowLeadingClick={onRowLeadingClick}
            selectionTracker={selectionTracker}
            onRowDeselect={onRowDeselect}
            multiSelectedRows={multiSelectedRows}
            renderSubTable={renderSubTable}
            actions={actions}
            applyLinkToAllCells={applyLinkToAllCells}
            dragToReorder={dragToReorder}
            isRowDraggable={isRowDraggable}
            onRowReorder={onRowReorder}
            enableMultiRowSelection={enableMultiRowSelection}
            isRowHoverable={isRowHoverable}
            context={context}
          />
        )}
      </Table>
    ),
    [
      table,
      selectionTracker,
      isNested,
      isMultiRowSelectionEnabled,
      variant,
      hasSubTable,
      rows,
      headerSelectionState,
      handleSelectAllToggle,
      dragToReorder,
      isVirtual,
      tableContainerRef,
      isRowDisabled,
      isRowSelected,
      isRowCollapsed,
      isRowHoverable,
      onToggleRowCollapse,
      getLinkTo,
      getDrawerLinkTo,
      getEditLinkTo,
      onRowClick,
      onRowLeadingClick,
      multiSelectedRows,
      renderSubTable,
      actions,
      applyLinkToAllCells,
      isRowDraggable,
      onRowReorder,
      enableMultiRowSelection,
      onRowDeselect,
      context,
      enableSorting,
      allRowsCollapsed,
    ],
  );

  if (emptyState && rows.length === 0) {
    return emptyState;
  }

  // Use single DndContext for both row and column drag-and-drop
  if (dragToReorder) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => {
          const draggedId = event.active.id as string;
          if (columnIds.includes(draggedId)) {
            // Column drag
          } else if (rowIds.includes(draggedId)) {
            // Row drag
            setIsDraggingRow(true);
          }
        }}
        onDragEnd={(event) => {
          const draggedId = event.active.id as string;
          if (columnIds.includes(draggedId)) {
            // Column drag end
          } else if (rowIds.includes(draggedId)) {
            // Row drag end
            setIsDraggingRow(false);

            handleDragEnd(event);
          }
        }}
      >
        <SortableContext
          items={(() => {
            if (isDraggingRow) return rowIds;
            const items: string[] = [];
            if (dragToReorder) items.push(...rowIds);
            return items;
          })()}
          strategy={verticalListSortingStrategy}
        >
          {tableContent}
        </SortableContext>
      </DndContext>
    );
  }

  return tableContent;
}

export function AutoTableWithState<D extends any, C extends ColumnDef<D, any>>({
  tableState,
  data,
  columns,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  isRowDisabled,
  isRowSelected,
  onRowDeselect,
  onRowClick,
  onRowLeadingClick,
  enableSorting = true,
  enableMultiRowSelection = false,
  enableMultiRowExpansion = true,
  applyLinkToAllCells,
  exportToCSVFilename,
  onRowMultiSelectionChange,
  multiSelectedRows,
  getRowId,
  isNested,
  renderSubTable,
  subTableExpandedByDefault = false,
  isVirtual = undefined,
  tableContainerRef = undefined,
  onRowsChange,
  emptyState,
  variant,
  isRowDraggable,
  onRowReorder,
  isRowHoverable,
  actions,
  globalFilterFn = defaultGlobalFilterFn,
  context = 'application',
}: {
  tableState: AutoCollectionStateInterface<D, NonNullable<C['id']>>;
  data: D[];
  columns: C[];
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  onRowDeselect?: OnRowDeselect<D>;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  enableSorting?: boolean;
  enableRowSelection?: boolean | ((row: Row<D>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  enableMultiRowExpansion?: boolean;
  applyLinkToAllCells?: boolean;
  exportToCSVFilename?: string;
  onRowMultiSelectionChange?: OnChangeFn<RowSelectionState>;
  multiSelectedRows?: RowSelectionState;
  getRowId: ((originalRow: D, index: number, parent?: Row<D>) => string) | undefined;
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  subTableExpandedByDefault?: boolean;
  isVirtual?: boolean;
  tableContainerRef?: RefObject<HTMLDivElement | null>;
  onRowsChange?: (rows: Row<D>[]) => void;
  emptyState?: ReactNode;
  variant?: TableVariantProp;
  isRowDraggable?: IsRowDraggable<D>;
  onRowReorder?: OnRowReorder;
  isRowHoverable?: boolean;
  actions?: ReactNode;
  globalFilterFn?: FilterFnOption<D>;
  context?: GutterContext;
}) {
  const flexiContext = useFlexiColumnsContext();

  const effectiveOnRowLeadingClick =
    onRowLeadingClick ?? (flexiContext ? () => flexiContext.setActiveColumn('end') : undefined);
  const effectiveOnRowDeselect =
    onRowDeselect ??
    (flexiContext
      ? () => {
          flexiContext.setActiveColumn('start');
          if (flexiContext.onDeselect) {
            setTimeout(flexiContext.onDeselect, FLEXI_COLUMNS_ANIMATION_MS);
          }
        }
      : undefined);
  const effectiveIsRowSelected =
    isRowSelected ??
    (flexiContext && getRowId
      ? (row: D) => getRowId(row, 0) === (flexiContext.selectedId ?? null)
      : undefined);

  const isMultiRowSelectionEnabled = useMemo(() => {
    if (typeof enableMultiRowSelection === 'boolean') {
      return enableMultiRowSelection;
    }
    if (typeof enableMultiRowSelection === 'function') {
      return true; // Function-based selection is enabled
    }
    // For AccessLevelProp, we'll need to check permissions
    // For now, we'll assume it's enabled if it's an access level and not null
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection]);

  const state = useMemo(() => {
    const s: Partial<ReactTableState> = {
      globalFilter: tableState.globalFilter,
    };

    if (multiSelectedRows) {
      s.rowSelection = multiSelectedRows;
    }

    if (tableState.sortingState) {
      s.sorting = tableState.sortingState;
    }

    if (tableState.columnVisibility) {
      s.columnVisibility = tableState.columnVisibility as VisibilityState;
    }

    if (tableState.columnFilters) {
      s.columnFilters = tableState.columnFilters;
    }

    if (tableState.columnOrder) {
      s.columnOrder = tableState.columnOrder;
    }

    return s;
  }, [tableState, multiSelectedRows]);

  // Expand columns to include virtual subline filter columns
  const expandedColumnsForTable = useMemo(
    () => expandColumnsWithSublineFilters(columns),
    [columns],
  );

  const table = useReactTable({
    data,
    columns: expandedColumnsForTable,
    filterFns,
    sortingFns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: tableState.isServerSidePaginated ? undefined : getFilteredRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    enableGlobalFilter: true,
    getColumnCanGlobalFilter: defaultColumnCanGlobalFilter,
    globalFilterFn,
    onSortingChange: (value) => {
      if (typeof value === 'function') {
        tableState.setSortingState(value(tableState.sortingState));
      } else {
        tableState.setSortingState(value);
      }
    },
    enableMultiRowSelection: isMultiRowSelectionEnabled,
    onRowSelectionChange: onRowMultiSelectionChange,
    getRowId,
    state,
    onColumnFiltersChange: (value) => {
      if (typeof value === 'function') {
        tableState.setColumnFilters(value(tableState.columnFilters));
      } else {
        tableState.setColumnFilters(value);
      }
    },
    onColumnOrderChange: (value) => {
      if (typeof value === 'function') {
        tableState.setColumnOrder(value(tableState.columnOrder));
      } else {
        tableState.setColumnOrder(value);
      }
    },
  });

  const filteredRows = getRows(table);

  const selectionTracker = useSelectionTracker();

  // Computed selection state for the header checkbox
  const headerSelectionState = useMemo(
    () =>
      createHeaderSelectionState(table, filteredRows, enableMultiRowSelection, multiSelectedRows),
    [table, filteredRows, enableMultiRowSelection, multiSelectedRows],
  );

  const handleSelectAllToggle = useCallback(
    () => selectAllToggleHandler(table, enableMultiRowSelection, multiSelectedRows),
    [table, enableMultiRowSelection, multiSelectedRows],
  );

  // Add hotkey handler for select all when multi-row selection is enabled
  useHotkeys(
    'alt+a',
    (e) => {
      if (!isMultiRowSelectionEnabled) return;
      e.preventDefault();

      // Trigger select all functionality
      handleSelectAllToggle();
    },
    {
      enableOnFormTags: true,
    },
    [isMultiRowSelectionEnabled, handleSelectAllToggle],
  );

  // Use shared CSV export handler
  const exportToCSV = useMemo(
    () => createCSVExportHandler(table, exportToCSVFilename),
    [table, exportToCSVFilename],
  );

  // Use shared column visibility effect
  const defaultColumnVisibilityEffect = useCallback(
    () =>
      createDefaultColumnVisibilityEffect(
        expandedColumnsForTable,
        filteredRows,
        tableState.setDefaultColumnVisibility as React.Dispatch<
          React.SetStateAction<VisibilityState>
        >,
        tableState.defaultColumnVisibility as VisibilityState,
      ),
    [
      expandedColumnsForTable,
      filteredRows,
      tableState.setDefaultColumnVisibility,
      tableState.defaultColumnVisibility,
    ],
  );

  useEffect(() => {
    defaultColumnVisibilityEffect();
  }, [defaultColumnVisibilityEffect]);

  useImperativeHandle(
    tableState.tableRef,
    () => ({
      exportToCSV,
      filteredRows,
      data,
    }),
    [exportToCSV, filteredRows, data],
  );

  const { setNumRows } = tableState;
  useEffect(() => {
    if (!tableState.isServerSidePaginated) {
      setNumRows(filteredRows.length);
    }
    onRowsChange?.(filteredRows);
  }, [filteredRows, onRowsChange, setNumRows, tableState.isServerSidePaginated]);

  const rows = useMemo(
    () =>
      tableState.offset != null && tableState.limit != null && !tableState.isServerSidePaginated
        ? filteredRows.slice(
            tableState.offset,
            tableState.limit ? tableState.offset + tableState.limit : undefined,
          )
        : filteredRows,
    [filteredRows, tableState.offset, tableState.limit, tableState.isServerSidePaginated],
  );

  const hasSubTable = renderSubTable;
  const [expandedRowsSet, setExpandedRowsSet] = useState(() =>
    subTableExpandedByDefault ? new Set(rows.map((row) => row.id)) : new Set(),
  );

  // Header expand/collapse all: show collapsed (chevron-right) when all rows are collapsed
  const allRowsCollapsed = expandedRowsSet.size === 0;

  useEffect(() => {
    if (subTableExpandedByDefault) {
      setExpandedRowsSet(() => new Set(rows.map((row) => row.id)));
    }
  }, [subTableExpandedByDefault, rows]);

  const onToggleRowCollapse = useCallback(
    (row: Row<D>) => {
      const newExpandedSet = !enableMultiRowExpansion ? new Set() : new Set([...expandedRowsSet]);
      if (expandedRowsSet.has(row.id)) {
        newExpandedSet.delete(row.id);
      } else {
        newExpandedSet.add(row.id);
      }
      setExpandedRowsSet(newExpandedSet);
    },
    [enableMultiRowExpansion, expandedRowsSet],
  );

  const isRowCollapsed = useCallback(
    (row: Row<D>) => !expandedRowsSet.has(row.id),
    [expandedRowsSet],
  );

  // DnD sensors - hooks must be called directly, not in useMemo
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const rowIds = useMemo(() => rows.map((row) => row.id), [rows]);

  const handleRowReorder = onRowReorder ?? tableState.handleRowReorder;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!handleRowReorder || !over || active.id === over.id) {
        return;
      }

      const activeIndex = rows.findIndex((row) => row.id === active.id);
      const overIndex = rows.findIndex((row) => row.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        handleRowReorder(activeIndex, overIndex);
      }
    },
    [handleRowReorder, rows],
  );

  const [isDraggingRow, setIsDraggingRow] = useState(false);

  // Add hotkey handler for select all when multi-row selection is enabled
  useHotkeys(
    'alt+a',
    (e) => {
      if (!isMultiRowSelectionEnabled) return;
      e.preventDefault();

      // Trigger select all functionality
      handleSelectAllToggle();
    },
    {
      enableOnFormTags: true,
    },
    [isMultiRowSelectionEnabled, handleSelectAllToggle],
  );

  if (emptyState && rows.length === 0) {
    return emptyState;
  }

  const tableContent = (
    <Table isNested={isNested} multiRowSelection={!!isMultiRowSelectionEnabled} variant={variant}>
      <TableHead>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableHeadRow key={headerGroup.id}>
            <TableCellBuffer context={context} side="leading" head />
            <TableCellState side="leading" head />
            {hasSubTable && (
              <TableHeadCell
                collapsable
                onClick={() => {
                  if (allRowsCollapsed) {
                    setExpandedRowsSet(new Set([...rows.map((row) => row.id)]));
                  } else {
                    setExpandedRowsSet(new Set());
                  }
                }}
                collapsed={allRowsCollapsed}
                stuck="left"
              />
            )}
            {isMultiRowSelectionEnabled && (
              <AutoTableHeadCellSelector
                key={SELECT_COLUMN_ID}
                hideSortIcon
                stuck="left"
                hasCollapsableBefore={!!hasSubTable}
              >
                <AutoTableHeaderSelector
                  headerSelectionState={headerSelectionState}
                  onToggle={handleSelectAllToggle}
                  selectionTracker={selectionTracker}
                />
              </AutoTableHeadCellSelector>
            )}
            {tableState.enableRowReordering && <AutoTableHeadCellDrag />}
            {headerGroup.headers.map((header) => {
              // Skip the multi-select cell since we render it separately
              if (header.column.id === SELECT_COLUMN_ID) {
                return null;
              }
              if (header.column.columnDef.meta?.accessLevel === null) {
                return null;
              }
              return (
                <TableHeadCell
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  {...getSizeProps(table, header.column)}
                  sortDirection={enableSorting ? header.column.getIsSorted() : undefined}
                  hideSortIcon={!enableSorting}
                  accessLevel={header.column.columnDef.meta?.accessLevel}
                  isLeading={header.column.columnDef.meta?.isLeading}
                  stuck={header.column.columnDef.meta?.stuck}
                  tooltip={header.column.columnDef.meta?.tooltip}
                  hasCollapsableBefore={!!hasSubTable}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeadCell>
              );
            })}
            <TableCellState side="trailing" head />
            <TableCellBuffer context={context} side="trailing" head />
          </TableHeadRow>
        ))}
      </TableHead>
      {isVirtual && tableContainerRef ? (
        <VirtualAutoTableBody
          table={table}
          rows={rows}
          tableContainerRef={tableContainerRef}
          isRowDisabled={isRowDisabled}
          isRowSelected={effectiveIsRowSelected}
          isRowCollapsed={isRowCollapsed}
          onToggleRowCollapse={onToggleRowCollapse}
          isNested={isNested}
          getLinkTo={getLinkTo}
          getDrawerLinkTo={getDrawerLinkTo}
          getEditLinkTo={getEditLinkTo}
          onRowClick={onRowClick}
          onRowLeadingClick={effectiveOnRowLeadingClick}
          selectionTracker={selectionTracker}
          onRowDeselect={effectiveOnRowDeselect}
          multiSelectedRows={multiSelectedRows}
          renderSubTable={renderSubTable}
          actions={actions}
          applyLinkToAllCells={applyLinkToAllCells}
          dragToReorder={tableState.enableRowReordering}
          isRowDraggable={isRowDraggable}
          onRowReorder={onRowReorder}
          enableMultiRowSelection={enableMultiRowSelection}
          isRowHoverable={isRowHoverable}
          context={context}
        />
      ) : (
        <AutoTableBody
          table={table}
          rows={rows}
          isRowDisabled={isRowDisabled}
          isRowSelected={effectiveIsRowSelected}
          isRowCollapsed={isRowCollapsed}
          onToggleRowCollapse={onToggleRowCollapse}
          isNested={isNested}
          getLinkTo={getLinkTo}
          getDrawerLinkTo={getDrawerLinkTo}
          getEditLinkTo={getEditLinkTo}
          onRowClick={onRowClick}
          onRowLeadingClick={effectiveOnRowLeadingClick}
          selectionTracker={selectionTracker}
          onRowDeselect={effectiveOnRowDeselect}
          multiSelectedRows={multiSelectedRows}
          renderSubTable={renderSubTable}
          actions={actions}
          applyLinkToAllCells={applyLinkToAllCells}
          dragToReorder={tableState.enableRowReordering}
          isRowDraggable={isRowDraggable}
          onRowReorder={onRowReorder}
          enableMultiRowSelection={enableMultiRowSelection}
          isRowHoverable={isRowHoverable}
          context={context}
        />
      )}
    </Table>
  );

  if (tableState.enableRowReordering) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => {
          setIsDraggingRow(true);
        }}
        onDragEnd={(event) => {
          setIsDraggingRow(false);
          handleDragEnd(event);
        }}
      >
        <SortableContext
          items={(() => {
            if (isDraggingRow) return rowIds;
            // When not dragging, only include items for enabled features
            const items: string[] = [];
            if (tableState.enableRowReordering) items.push(...rowIds);
            return items;
          })()}
          strategy={verticalListSortingStrategy}
        >
          {tableContent}
        </SortableContext>
      </DndContext>
    );
  }

  return tableContent;
}
